import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  flip,
  offset,
  type Placement,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import clsx from "clsx";
import {
  cloneElement,
  createContext,
  use,
  useEffect,
  useRef,
  useState,
} from "react";
import { getClosestLayer } from "@/utils/dom";
import styles from "./Tooltip.module.css";

interface TooltipProps {
  open?: boolean;
  dismiss?: number;
  trigger?: "click" | "hover";
  placement?: Placement;
  showArrow?: boolean;
  disabled?: boolean;
  interactive?: boolean;
  onClose?: () => void;
}

export const Tooltip: React.FC<React.PropsWithChildren<TooltipProps>> & {
  Toggle: typeof Toggle;
  Popup: typeof Popup;
  Group: typeof Group;
  Text: typeof Text;
} = (props) => {
  if (props.disabled === true) {
    return (
      <TooltipContext.Provider
        value={{
          open: props.open ?? false,
          showArrow: props.showArrow ?? false,
          disabled: props.disabled,
          interactive: props.disabled ?? true,
          onClose: props.onClose,
        }}
      >
        {props.children}
      </TooltipContext.Provider>
    );
  }

  return <TooltipContainer {...props} />;
};

const TooltipContainer: React.FC<React.PropsWithChildren<TooltipProps>> = ({
  open: initialOpen = false,
  dismiss,
  trigger = "hover",
  placement = "top",
  showArrow = true,
  disabled = false,
  interactive = true,
  onClose,
  children,
}) => {
  const [open, setOpen] = useState(initialOpen);

  const arrowRef = useRef<SVGSVGElement>(null);

  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "fixed",
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(12),
      flip(),
      shift({
        padding: 12,
      }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const interactions = useInteractions([
    useHover(floating.context, {
      enabled: trigger === "hover",
      handleClose: interactive === true ? safePolygon() : undefined,
    }),
    useClick(floating.context, {
      enabled: trigger === "click",
    }),
    useDismiss(floating.context, {
      enabled: trigger === "click",
    }),
    useRole(floating.context, {
      role: "tooltip",
    }),
  ]);

  useEffect(() => {
    if (dismiss !== undefined && open === true) {
      const timeout = setTimeout(() => {
        setOpen(false);
      }, dismiss);

      return () => {
        clearTimeout(timeout);
      };
    }

    return () => {};
  }, [dismiss, open]);

  return (
    <TooltipContext.Provider
      value={{
        open,
        showArrow,
        disabled,
        interactive,
        onClose,
      }}
    >
      <TooltipFloatingContext.Provider
        value={{
          arrowRef,
          floating,
          interactions,
        }}
      >
        {children}
      </TooltipFloatingContext.Provider>
    </TooltipContext.Provider>
  );
};

interface TooltipContextProps {
  open: boolean;
  showArrow: boolean;
  disabled: boolean;
  interactive: boolean;
  onClose?: () => void;
}

const TooltipContext = createContext<TooltipContextProps | null>(null);

interface TooltipFloatingContextProps {
  arrowRef: React.RefObject<SVGSVGElement | null>;
  floating: ReturnType<typeof useFloating>;
  interactions: ReturnType<typeof useInteractions>;
}

const TooltipFloatingContext =
  createContext<TooltipFloatingContextProps | null>(null);

const useTooltipContext = () => {
  const context = use(TooltipContext);

  if (context === null) {
    throw new Error(
      "useTooltipContext must be used within a TooltipContext.Provider",
    );
  }

  return context;
};

const useTooltipFloatingContext = () => {
  const context = use(TooltipFloatingContext);

  if (context === null) {
    throw new Error(
      "useTooltipFloatingContext must be used within a useTooltipFloatingContext.Provider",
    );
  }

  return context;
};

const Toggle: React.FC<{
  children: React.ReactElement<{}> & {
    ref?: React.RefObject<Element>;
  };
}> = ({ children }) => {
  const { disabled } = useTooltipContext();

  if (disabled === true) {
    return children;
  }

  const { floating, interactions } = useTooltipFloatingContext();

  const childrenClone = cloneElement(
    children,
    interactions.getReferenceProps({
      ref: useMergeRefs([floating.refs.setReference, children.ref]),
      ...children.props,
    }),
  );

  return childrenClone;
};

const Popup: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = (props) => {
  const { disabled } = useTooltipContext();

  if (disabled === true) {
    return null;
  }

  return <PopupFloating {...props} />;
};

const PopupFloating: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ children, className }) => {
  const { showArrow, interactive, onClose } = useTooltipContext();
  const { floating, interactions } = useTooltipFloatingContext();

  const transitionStyles = useTransitionStyles(floating.context, {
    initial: () => ({
      opacity: 0,
    }),
  });

  useEffect(() => {
    if (transitionStyles.isMounted === false) {
      onClose?.();
    }
  }, [transitionStyles.isMounted, onClose]);

  return (
    <>
      {transitionStyles.isMounted === true && (
        <FloatingPortal root={getClosestLayer(floating.elements.domReference)}>
          <div
            ref={floating.refs.setFloating}
            className={styles.popup}
            style={{
              ...floating.floatingStyles,
              pointerEvents: interactive === false ? "none" : undefined,
            }}
            {...interactions.getFloatingProps()}
          >
            <div style={{ ...transitionStyles.styles }}>
              <div className={clsx(styles.tooltip, className)}>{children}</div>
              {showArrow && <TooltipArrow />}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

const TooltipArrow = () => {
  const { arrowRef, floating } = useTooltipFloatingContext();

  return (
    <FloatingArrow
      ref={arrowRef}
      context={floating.context}
      className={styles.arrow}
      width={40}
      height={40}
      preserveAspectRatio="xMidYMax"
      d="M40 40H0H6.56072C9.54592 40 12.4239 38.8786 14.6328 36.8549L17.982 33.7863C19.1262 32.7379 20.8738 32.7379 22.018 33.7863L25.3672 36.8549C27.5761 38.8786 30.4541 40 33.4393 40H40Z"
    />
  );
};

interface TooltipGroupProps extends React.HTMLAttributes<HTMLDivElement> {}

const Group: React.FC<TooltipGroupProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div className={clsx(styles["tooltip-group"], className)} {...props}>
      {children}
    </div>
  );
};

interface TooltipTextProps extends React.HTMLAttributes<HTMLDivElement> {}

const Text: React.FC<TooltipTextProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div className={clsx(styles["tooltip-text"], className)} {...props}>
      {children}
    </div>
  );
};

Tooltip.Toggle = Toggle;
Tooltip.Popup = Popup;
Tooltip.Group = Group;
Tooltip.Text = Text;
